import { useEffect, useMemo, useState } from 'react'
import { Button, Input } from '@agms/ui'
import {
  useDocuments,
  useUploadDocument,
  useDeleteDocument,
  useBulkDeleteDocuments,
  useBulkDownloadDocuments,
  useDocumentPreview,
  usePdfTemplates,
  useGeneratePdf,
  type DocumentAnchor,
} from '../../hooks/useDocuments'
import { usePermissions } from '../../hooks/useAuth'
import type { ApiError, LeadDocument, PdfTemplateOption } from '../../lib/api'
import type { Merchant } from '../../pages/merchants/useMerchantData'
import { FileUploadZone } from './FileUploadZone'
import { RequestDocumentsModal } from './RequestDocumentsModal'
import { EmailDocumentModal } from './EmailDocumentModal'
import { CardTitle, SurfaceCard } from '../ui'

export interface DocumentCenterLead {
  id: string
  dba_name: string | null
  contact_name?: string | null
  business_email?: string | null
  application?: { owner_email?: string | null } | null
}

export type DocumentCenterContext =
  | { kind: 'lead'; lead: DocumentCenterLead }
  | { kind: 'merchant'; merchant: Merchant }

interface Props {
  context: DocumentCenterContext
}

// Accept PDFs + common images. The legacy category-based MIME rules
// only apply to the merchant upload flow (MerchantDocumentUploadController),
// which is intentionally unchanged here.
const UPLOAD_ACCEPT = 'application/pdf,image/jpeg,image/png,image/heic'

function formatFileSize(bytes: number | null): string {
  if (bytes === null) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ''
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function uploadErrorMessage(err: unknown): string {
  const apiError = err as ApiError
  const response = apiError.response?.data
  const firstValidationError = response?.errors
    ? Object.values(response.errors).flat().find(Boolean)
    : undefined

  return firstValidationError ?? response?.message ?? apiError.message ?? 'Upload failed.'
}

/**
 * Small debounce so the list query doesn't fire on every keystroke.
 * 250 ms matches the backend ILIKE cost comfortably.
 */
function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

function contextAnchor(context: DocumentCenterContext): DocumentAnchor {
  return context.kind === 'lead'
    ? { kind: 'lead', id: context.lead.id }
    : { kind: 'merchant', id: context.merchant.id }
}

function requestDefaults(context: DocumentCenterContext): { email: string | null; name: string | null } {
  if (context.kind === 'lead') {
    return {
      email: context.lead.application?.owner_email ?? context.lead.business_email ?? null,
      name: context.lead.dba_name ?? context.lead.contact_name ?? null,
    }
  }

  return {
    email: context.merchant.business_email,
    name: context.merchant.dba_name,
  }
}

export function DocumentCenter({ context }: Props) {
  const [search, setSearch] = useState('')
  const [activeTool, setActiveTool] = useState<'upload' | 'generate' | null>(null)
  const debouncedSearch = useDebounced(search)
  const anchor = useMemo(() => contextAnchor(context), [context])
  const defaults = useMemo(() => requestDefaults(context), [context])

  const { data, isLoading, error } = useDocuments(anchor, debouncedSearch)
  const uploadDoc = useUploadDocument()
  const deleteDoc = useDeleteDocument()
  const bulkDelete = useBulkDeleteDocuments()
  const bulkDownload = useBulkDownloadDocuments()
  const preview = useDocumentPreview()
  const {
    canRequestDocuments,
    canDeleteDocuments,
    canUploadRestricted,
    canEmailDocuments,
    canGeneratePdf,
  } = usePermissions()

  const canUseLeadPdfTools = context.kind === 'lead' && canGeneratePdf()
  const leadId = context.kind === 'lead' ? context.lead.id : undefined
  const pdfTemplatesQuery = usePdfTemplates(canUseLeadPdfTools ? leadId : undefined)
  const generatePdf = useGeneratePdf()

  // Upload form
  const [label, setLabel] = useState('')
  const [isRestricted, setIsRestricted] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  // Selection
  const [selected, setSelected] = useState<Set<string>>(new Set())

  // Modals
  const [showRequestModal, setShowRequestModal] = useState(false)
  const [emailDocument, setEmailDocument] = useState<LeadDocument | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false)

  // Generate PDF
  const [pdfTemplateKey, setPdfTemplateKey] = useState('')
  const [pdfLabelOverride, setPdfLabelOverride] = useState('')

  const documents = data?.documents ?? []
  const suggestedLabels = data?.suggested_labels ?? []
  const restrictedDefaultLabels = useMemo(
    () => new Set(data?.restricted_default_labels ?? []),
    [data?.restricted_default_labels],
  )

  // If the label matches a known-restricted default (e.g. "Voided Check"),
  // auto-check the restricted box for ops — easy to uncheck if they
  // disagree. We only nudge; we never override a manual choice after
  // they've interacted with the checkbox.
  const [restrictedTouched, setRestrictedTouched] = useState(false)
  useEffect(() => {
    if (restrictedTouched) return
    if (!label) return
    if (restrictedDefaultLabels.has(label.trim())) {
      setIsRestricted(true)
    }
  }, [label, restrictedDefaultLabels, restrictedTouched])

  // Keep selection stable: drop ids that disappeared from the list.
  useEffect(() => {
    if (selected.size === 0) return
    const valid = new Set(documents.map((d) => d.id))
    let changed = false
    const next = new Set<string>()
    for (const id of selected) {
      if (valid.has(id)) next.add(id)
      else changed = true
    }
    if (changed) setSelected(next)
  }, [documents, selected])

  // Only non-synthetic rows are bulk-actionable (you can't bulk-delete the
  // BoldSign-provided signed MPA synthetic row).
  const selectableDocuments = useMemo(
    () => documents.filter((d) => !d.is_synthetic),
    [documents],
  )

  const allSelectableSelected =
    selectableDocuments.length > 0 &&
    selectableDocuments.every((d) => selected.has(d.id))

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    if (allSelectableSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(selectableDocuments.map((d) => d.id)))
    }
  }

  const handleUpload = (file: File) => {
    setUploadError(null)
    const trimmedLabel = label.trim()
    if (!trimmedLabel) {
      setUploadError('Enter a label before uploading.')
      return
    }
    setUploading(true)
    uploadDoc.mutate(
      {
        anchor,
        payload: {
          file,
          label: trimmedLabel,
          is_restricted: isRestricted,
        },
      },
      {
        onSuccess: () => {
          setLabel('')
          setIsRestricted(false)
          setRestrictedTouched(false)
          setActiveTool(null)
        },
        onError: (err: unknown) => {
          setUploadError(uploadErrorMessage(err))
        },
        onSettled: () => setUploading(false),
      },
    )
  }

  const handleSingleDelete = (id: string) => {
    deleteDoc.mutate(id, {
      onSettled: () => setDeleteConfirm(null),
    })
  }

  const handleBulkDelete = () => {
    const ids = Array.from(selected).filter((id) =>
      selectableDocuments.some((d) => d.id === id),
    )
    if (ids.length === 0) {
      setBulkDeleteConfirm(false)
      return
    }
    bulkDelete.mutate(
      { anchor, ids },
      {
        onSuccess: () => {
          setSelected(new Set())
          setBulkDeleteConfirm(false)
        },
        onSettled: () => setBulkDeleteConfirm(false),
      },
    )
  }

  const handleBulkDownload = () => {
    const ids = Array.from(selected).filter((id) =>
      selectableDocuments.some((d) => d.id === id),
    )
    if (ids.length === 0) return
    bulkDownload.mutate(
      { anchor, ids },
      {
        onSuccess: (resp) => {
          window.open(resp.download_url, '_blank', 'noopener,noreferrer')
        },
      },
    )
  }

  const handlePreview = (id: string) => {
    preview.mutate(id, {
      onSuccess: (resp) => {
        window.open(resp.preview_url, '_blank', 'noopener,noreferrer')
      },
    })
  }

  const handleGeneratePdf = () => {
    if (!pdfTemplateKey) return
    if (context.kind !== 'lead') return
    generatePdf.mutate(
      {
        leadId: context.lead.id,
        template_key: pdfTemplateKey,
        label: pdfLabelOverride.trim() || undefined,
      },
      {
        onSuccess: () => {
          setPdfTemplateKey('')
          setPdfLabelOverride('')
          setActiveTool(null)
        },
      },
    )
  }

  const selectedCount = Array.from(selected).filter((id) =>
    selectableDocuments.some((d) => d.id === id),
  ).length

  const templates: PdfTemplateOption[] = pdfTemplatesQuery.data?.templates ?? []
  const activeTemplate = templates.find((t) => t.key === pdfTemplateKey)
  const canShowGenerateTool = canUseLeadPdfTools && templates.length > 0
  const toggleTool = (tool: 'upload' | 'generate') => {
    setActiveTool((current) => (current === tool ? null : tool))
  }

  return (
    <>
      <SurfaceCard>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <CardTitle>Document Center</CardTitle>
              {documents.length > 0 && (
                <span className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                  {documents.length}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={activeTool === 'upload' ? 'primary' : 'secondary'}
                onClick={() => toggleTool('upload')}
              >
                Upload
              </Button>
              {canShowGenerateTool && (
                <Button
                  variant={activeTool === 'generate' ? 'primary' : 'secondary'}
                  onClick={() => toggleTool('generate')}
                >
                  Generate
                </Button>
              )}
              {canRequestDocuments() && (
                <Button variant="secondary" onClick={() => setShowRequestModal(true)}>
                  Request
                </Button>
              )}
            </div>
          </div>
        <div className="mt-4 space-y-4">
          {/* Upload section */}
          {activeTool === 'upload' && (
            <div className="rounded-md border border-blue-100 bg-blue-50/60 p-4">
              <div className="mb-3 flex items-start justify-between gap-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Upload a document</h4>
                  <p className="mt-1 text-xs text-gray-500">
                    Label it clearly so the team can find it later.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTool(null)}
                  className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-white/70 hover:text-gray-700"
                >
                  Close
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label htmlFor="document-upload-label" className="block text-sm font-medium text-gray-700 mb-1">
                    Label
                  </label>
                  <input
                    id="document-upload-label"
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Voided Check, Bank Statement — March, Driver's License"
                    list="document-label-suggestions"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  {suggestedLabels.length > 0 && (
                    <datalist id="document-label-suggestions">
                      {suggestedLabels.map((s) => (
                        <option key={s} value={s} />
                      ))}
                    </datalist>
                  )}
                </div>

                {canUploadRestricted() && (
                  <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isRestricted}
                      onChange={(e) => {
                        setIsRestricted(e.target.checked)
                        setRestrictedTouched(true)
                      }}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span>
                      <span className="font-medium">Restricted</span> — hide from sales rep,
                      support, and finance roles.
                    </span>
                  </label>
                )}

                <FileUploadZone
                  onFileSelect={handleUpload}
                  accept={UPLOAD_ACCEPT}
                  isUploading={uploading}
                  disabled={!label.trim()}
                />

                {uploadError && <p className="text-xs text-red-600" role="alert">{uploadError}</p>}
              </div>
            </div>
          )}

          {/* Generate PDF section */}
          {activeTool === 'generate' && canShowGenerateTool && (
            <div className="rounded-md border border-teal-100 bg-teal-50/60 p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">Generate from lead data</h4>
                  <p className="mt-1 text-xs text-gray-500">Pre-fills merchant details into a reusable template.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setActiveTool(null)}
                  className="rounded px-2 py-1 text-xs font-medium text-gray-500 hover:bg-white/70 hover:text-gray-700"
                >
                  Close
                </button>
              </div>
              <div className="grid gap-3 md:grid-cols-[1fr,1fr,auto] md:items-end">
                <div>
                  <label htmlFor="document-template" className="block text-sm font-medium text-gray-700 mb-1">Template</label>
                  <select
                    id="document-template"
                    value={pdfTemplateKey}
                    onChange={(e) => setPdfTemplateKey(e.target.value)}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Choose a template…</option>
                    {templates.map((t) => (
                      <option key={t.key} value={t.key}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="document-template-label" className="block text-sm font-medium text-gray-700 mb-1">
                    Label (optional)
                  </label>
                  <input
                    id="document-template-label"
                    type="text"
                    value={pdfLabelOverride}
                    onChange={(e) => setPdfLabelOverride(e.target.value)}
                    placeholder={activeTemplate?.label ?? 'Defaults to template label'}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <Button
                  onClick={handleGeneratePdf}
                  disabled={!pdfTemplateKey}
                  isLoading={generatePdf.isPending}
                >
                  Generate
                </Button>
              </div>
              {activeTemplate && (
                <p className="mt-2 text-xs text-gray-500">{activeTemplate.summary}</p>
              )}
              {generatePdf.isError && (
                <p className="mt-2 text-xs text-red-600" role="alert">
                  Failed to generate. Check lead has the required fields populated.
                </p>
              )}
            </div>
          )}

          <div className="rounded-md border border-slate-200 bg-slate-50/80 p-3">
            {/* Search + bulk action bar */}
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <div className="min-w-56 flex-1">
                <Input
                  type="search"
                  aria-label="Search documents"
                  placeholder="Search by file name or label..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              {selectedCount > 0 && (
                <>
                  <span className="text-xs text-gray-600 whitespace-nowrap">
                    {selectedCount} selected
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleBulkDownload}
                    isLoading={bulkDownload.isPending}
                  >
                    Download
                  </Button>
                  {canDeleteDocuments() && (
                    bulkDeleteConfirm ? (
                      <>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBulkDelete}
                          isLoading={bulkDelete.isPending}
                        >
                          Confirm delete
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setBulkDeleteConfirm(false)}
                        >
                          Cancel
                        </Button>
                      </>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => setBulkDeleteConfirm(true)}
                      >
                        Delete
                      </Button>
                    )
                  )}
                </>
              )}
            </div>

            {/* Document table */}
            {isLoading && <p className="text-sm text-gray-500 py-4 text-center" aria-live="polite">Loading...</p>}
            {error && (
              <p className="text-sm text-red-500 py-4 text-center" role="alert">Failed to load documents.</p>
            )}

            {!isLoading && !error && documents.length === 0 && (
              <p className="rounded-md border border-dashed border-slate-300 bg-white/70 py-6 text-center text-sm text-gray-500">
                {debouncedSearch
                  ? `No documents match "${debouncedSearch}".`
                  : context.kind === 'lead'
                    ? 'No documents yet. Use Upload, Generate, or Request to add one.'
                    : 'No documents yet. Use Upload or Request to add one.'}
              </p>
            )}

            {!isLoading && !error && documents.length > 0 && (
              <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-100 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="w-8 py-2 px-2 text-left">
                        <input
                          type="checkbox"
                          checked={allSelectableSelected}
                          onChange={toggleAll}
                          disabled={selectableDocuments.length === 0}
                          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          aria-label="Select all"
                        />
                      </th>
                      <th className="py-2 px-2 text-left font-medium">File</th>
                      <th className="py-2 px-2 text-left font-medium">Label</th>
                      <th className="py-2 px-2 text-left font-medium">Uploaded by</th>
                      <th className="py-2 px-2 text-left font-medium">Date</th>
                      <th className="py-2 px-2 text-right font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {documents.map((doc) => {
                      const isChecked = selected.has(doc.id)
                      const isSyntheticOrProtected = doc.is_synthetic
                      return (
                        <tr key={doc.id} className="hover:bg-slate-50">
                          <td className="py-2 px-2">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleOne(doc.id)}
                              disabled={isSyntheticOrProtected}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-40"
                              aria-label={`Select ${doc.file_name}`}
                            />
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-gray-900 truncate">{doc.file_name}</span>
                              {doc.version > 1 && (
                                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                  v{doc.version}
                                </span>
                              )}
                              {doc.uploaded_by_merchant && (
                                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-700">
                                  Merchant
                                </span>
                              )}
                              {doc.is_restricted && (
                                <span className="flex-shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-red-50 text-red-700">
                                  Restricted
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-400 mt-0.5">
                              {formatFileSize(doc.file_size)}
                            </div>
                          </td>
                          <td className="py-2 px-2 text-gray-700">{doc.label}</td>
                          <td className="py-2 px-2 text-gray-600">
                            {doc.uploaded_by_merchant ? 'Merchant' : doc.uploaded_by}
                          </td>
                          <td className="py-2 px-2 text-gray-600 whitespace-nowrap">
                            {formatDate(doc.created_at)}
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-end gap-3">
                              <button
                                onClick={() => handlePreview(doc.id)}
                                className="text-xs text-blue-600 hover:text-blue-800"
                                disabled={preview.isPending}
                              >
                                Preview
                              </button>
                              <a
                                href={doc.download_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:text-blue-800"
                              >
                                Download
                              </a>
                              {canEmailDocuments() && (
                                <button
                                  onClick={() => setEmailDocument(doc)}
                                  className="text-xs text-blue-600 hover:text-blue-800"
                                >
                                  Email
                                </button>
                              )}
                              {canDeleteDocuments() && !isSyntheticOrProtected && (
                                deleteConfirm === doc.id ? (
                                  <span className="flex items-center gap-1">
                                    <button
                                      onClick={() => handleSingleDelete(doc.id)}
                                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                                    >
                                      {deleteDoc.isPending ? 'Deleting…' : 'Confirm'}
                                    </button>
                                    <button
                                      onClick={() => setDeleteConfirm(null)}
                                      className="text-xs text-gray-400 hover:text-gray-600"
                                    >
                                      Cancel
                                    </button>
                                  </span>
                                ) : (
                                  <button
                                    onClick={() => setDeleteConfirm(doc.id)}
                                    className="text-xs text-gray-400 hover:text-red-600"
                                  >
                                    Delete
                                  </button>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </SurfaceCard>

      {showRequestModal && (
        <RequestDocumentsModal
          anchor={anchor}
          merchantEmail={defaults.email}
          merchantName={defaults.name}
          onClose={() => setShowRequestModal(false)}
        />
      )}

      {emailDocument && (
        <EmailDocumentModal
          document={emailDocument}
          onClose={() => setEmailDocument(null)}
        />
      )}
    </>
  )
}
